// src/pages/orderAssignment/components/ProductSearchFilter.jsx
import React, { useState, useRef, useEffect } from 'react';
import { Search, X, Package, Hash } from 'lucide-react';
import { searchProducts } from '../../../services/api/productSearch';
import { useLocalization } from '../../../contexts/LocalizationContext';
import styles from './ProductSearchFilter.module.css';

const ProductSearchFilter = ({ shopId, selectedProductIds = [], onChange }) => {
  const { t } = useLocalization();
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const searchTimeoutRef = useRef(null);
  const wrapperRef = useRef(null);

  useEffect(() => {
    // Load selected product details when component mounts or shopId changes
    if (selectedProductIds.length > 0 && shopId) {
      loadSelectedProducts();
    }
  }, [selectedProductIds, shopId]);

  useEffect(() => {
    // Close dropdown when clicking outside
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadSelectedProducts = async () => {
    try {
      const response = await searchProducts(shopId, '', {
        limit: 1000,
        page: 1
      });
      
      const selected = response.products.filter(p => 
        selectedProductIds.includes(p.productId)
      );
      
      setSelectedProducts(selected);
    } catch (error) {
      console.error('Failed to load selected products:', error);
    }
  };

  const performSearch = async (term) => {
    if (!term.trim() || !shopId) {
      setSearchResults([]);
      setShowResults(false);
      return;
    }
    
    try {
      setIsSearching(true);
      const response = await searchProducts(shopId, term.trim(), {
        limit: 20,
        page: 1
      });
      
      setSearchResults(response.products || []);
      setShowResults(true);
    } catch (error) {
      console.error('Product search error:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleSearchChange = (value) => {
    setSearchTerm(value);
    
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    searchTimeoutRef.current = setTimeout(() => {
      performSearch(value);
    }, 300);
  };

  const handleProductSelect = (product) => {
    const newSelected = [...selectedProducts, product];
    const newIds = [...selectedProductIds, product.productId];
    
    setSelectedProducts(newSelected);
    onChange(newIds);
    setSearchTerm('');
    setSearchResults([]);
    setShowResults(false);
  };

  const handleProductRemove = (productId) => {
    const newSelected = selectedProducts.filter(p => p.productId !== productId);
    const newIds = selectedProductIds.filter(id => id !== productId);
    
    setSelectedProducts(newSelected);
    onChange(newIds);
  };

  const handleClearAll = () => {
    setSelectedProducts([]);
    onChange([]);
  };

  return (
    <div className={styles.productSearchFilter} ref={wrapperRef}>
      {/* Selected Products */}
      {selectedProducts.length > 0 && (
        <div className={styles.selectedProducts}>
          {selectedProducts.map((product) => (
            <div 
              key={`${product.productId}-${product.variationId || 'simple'}`}
              className={styles.selectedProductTag}
            >
              <span className={styles.productName}>
                {product.displayName}
                {product.attributeDisplay && (
                  <span className={styles.productVariation}>
                    ({product.attributeDisplay})
                  </span>
                )}
              </span>
              <button
                type="button"
                onClick={() => handleProductRemove(product.productId)}
                className={styles.removeButton}
              >
                <X size={14} />
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={handleClearAll}
            className={styles.clearAllButton}
          >
            {t('common.clearAll')}
          </button>
        </div>
      )}

      {/* Search Input */}
      <div className={styles.searchWrapper}>
        <div className={styles.searchInputWrapper}>
          <Search className={styles.searchIcon} size={16} />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => handleSearchChange(e.target.value)}
            onFocus={() => searchResults.length > 0 && setShowResults(true)}
            placeholder={t('orderAssignment.searchProducts')}
            className={styles.searchInput}
            disabled={!shopId}
          />
          {searchTerm && (
            <button
              type="button"
              onClick={() => {
                setSearchTerm('');
                setSearchResults([]);
                setShowResults(false);
              }}
              className={styles.clearSearchButton}
            >
              <X size={14} />
            </button>
          )}
        </div>

        {/* Search Results Dropdown */}
        {showResults && (
          <div className={styles.searchResults}>
            {isSearching ? (
              <div className={styles.searchLoading}>
                <div className={styles.loadingSpinner}></div>
                <span>{t('common.loading')}</span>
              </div>
            ) : searchResults.length > 0 ? (
              <div className={styles.resultsList}>
                {searchResults
                  .filter(product => !selectedProductIds.includes(product.productId))
                  .map((product) => (
                    <div
                      key={`${product.productId}-${product.variationId || 'simple'}`}
                      className={styles.resultItem}
                      onClick={() => handleProductSelect(product)}
                    >
                      <div className={styles.productInfo}>
                        <div className={styles.productNameResult}>
                          <Package size={14} />
                          {product.displayName}
                          {product.attributeDisplay && (
                            <span className={styles.productVariationResult}>
                              ({product.attributeDisplay})
                            </span>
                          )}
                        </div>
                        <div className={styles.productDetails}>
                          <span className={styles.productSku}>
                            <Hash size={12} />
                            {product.sku || 'No SKU'}
                          </span>
                          <span className={styles.productStock}>
                            {product.stockQuantity > 0 
                              ? `${product.stockQuantity} in stock` 
                              : 'Out of stock'}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            ) : (
              <div className={styles.noResults}>
                <Package size={16} />
                <span>{t('ecomOrders.noProductsFound')}</span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductSearchFilter;